import { getApiGalaxyServer, postApiGalaxyServer } from 'services';
import { RecentErrorsBodyType, TelemetryLastUpdatesBodyType } from 'types';
import {
  FetchPendingEntitiesParams,
  fetchNetworkLogParams,
  fetchTeamNetworkErrorLogParams,
  FetchIntegrationsParams,
  fetchIntegrationHealthByServiceNameParams,
  fetchIntegrationHealthParams,
  FetchCsvImportStatusParams,
  FetchCsvImportStatusPayload,
  UpdateIntegrationNotesParams,
  FetchEntityErrorsParams,
  FetchDataCountsByDataTypeParams,
  FetchDataOverviewPerDataTypesPayload,
  FetchEntityMappingsParams,
  UnarchiveIntegrationPayload,
  FetchPendingEntityStatusPayload,
  FetchIntegrationStoredProceduresPayload,
  FetchAgentTokenPayload,
  CheckIntegrationConnectionPayload,
} from 'IntegrationModule/types';
import omit from 'lodash/omit';

/**
 *
 * WARNING: Calling Integration server API will not work if you don't have CORS disabled
 * For development, try using this add-on
 * https://chrome.google.com/webstore/detail/allow-cors-access-control/lhobafahddgcelffkeicbaginigeejlf/related?hl=en
 */

export const getTeamIntegrationList = (
  mosaicTeamID: number,
  config: Record<string, any> = {},
  token = '',
) => getApiGalaxyServer(`/integrations_api/telemetry/team/${mosaicTeamID}`, config, token);

// Remove after QBD is fully deprecated
export const checkIntegrationConnection = ({
  params: { targetService, teamId },
  token,
  config,
}: BaseApiParams<CheckIntegrationConnectionPayload>) =>
  getApiGalaxyServer(
    `/integrations_api/forward/${targetService}/provision-company/${teamId}`,
    config,
    token,
  );

// Remove after QBD is fully deprecated
export const getAgentToken = ({
  params: { targetService, teamId },
  token,
  config,
}: BaseApiParams<FetchAgentTokenPayload>) =>
  getApiGalaxyServer(
    `/integrations_api/forward/${targetService}/provision-account/${teamId}`,
    config,
    token,
  );

export const getDataHistoryPerDataTypeByTeamID = (
  mosaicTeamID: number,
  options: {
    interval: string;
    limit?: number;
    offset: number;
  } = {
    interval: 'day',
    limit: 12,
    offset: 0,
  },
  config: Record<string, any> = {},
  token = '',
) =>
  getApiGalaxyServer(
    `/integrations_api/telemetry/dataHistory/${mosaicTeamID}/${options.interval}/${options.limit}/${options.offset}`,
    config,
    token,
  );

export const getIntegrationServiceMetrics = (
  {
    targetService,
    offset,
    limit,
    date,
  }: {
    targetService: string;
    offset: number;
    limit: number;
    date: string; // In YYYY-MM-DD format
  },
  config: Record<string, any> = {},
  token = '',
) =>
  getApiGalaxyServer(
    `/integrations_api/telemetry/serviceMetrics/${targetService}/${limit}/${offset}/${date}`,
    config,
    token,
  );

export const getIntegrationServiceMetricsTotal = (
  targetService: string,
  startDate: string, // In YYYY-MM-DD format
  config: Record<string, any> = {},
  token = '',
) =>
  getApiGalaxyServer(
    `/integrations_api/telemetry/integrationServiceMetrics/${targetService}/${startDate}`,
    config,
    token,
  );

/**
 * A post request that retrieves body overview for all targetServicesIDs passed in the array
 * @param body
 *  @key targetServiceIds
 *  @type Array<number>
 * @param config
 * @returns
 */
export const postDataOverviewPerDataTypes = (
  body: FetchDataOverviewPerDataTypesPayload,
  config = {},
  token?: string,
) => postApiGalaxyServer('/integrations_api/telemetry/dataOverview', body, config, token);

/**
 * A post request that retrieves most recent errors for a specific integration target service
 * @param body
 *  @key
 */

export const postRecentErrors = (
  body: RecentErrorsBodyType,
  config: Record<string, any> = {},
  token?: string,
) => postApiGalaxyServer('/integrations_api/telemetry/recentErrors', body, config, token);

/**
 * A post request that retrieves most recent errors for all target service ids
 * The only
 * @param body
 *  @key
 */

export const postGeneralRecentErrors = (
  body: RecentErrorsBodyType,
  config: Record<string, any> = {},
  token?: string,
) => postApiGalaxyServer('/integrations_api/telemetry/recentErrors', body, config, token);

/**
 * A post request that retrieves latest update from IS for various body types
 *
 */

export const postTelemetryLastUpdates = (
  body: TelemetryLastUpdatesBodyType,
  config: Record<string, any> = {},
  token?: string,
) => postApiGalaxyServer('/integrations_api/telemetry/lastUpdates', body, config, token);

/**
 * A GET request that retrieves integration server's queues status
 */

export const getIntegrationServerQueuesStatus = (config: Record<string, any> = {}, token = '') =>
  getApiGalaxyServer('/integrations_api/telemetry/queueStatus', config, token);

export const getPendingEntities = (
  body: Omit<FetchPendingEntitiesParams, 'day' | 'onSuccess' | 'onFailure'>,
  config = {},
  token?: string,
) => postApiGalaxyServer('/integrations_api/telemetry/pendingEntities', body, config, token);

export const getNetworkLog = (params: fetchNetworkLogParams, config = {}, token?: string) => {
  const { mosaicTeamId, sessionId, startDate } = params;

  return getApiGalaxyServer(
    `/integrations_api/telemetry/findNetworkLog/${mosaicTeamId}/${sessionId}/${startDate}`,
    config,
    token,
  );
};

export const getNetworkErrorLog = (
  body: Omit<fetchTeamNetworkErrorLogParams, 'isInitialFetch'>,
  config = {},
  token?: string,
) => postApiGalaxyServer(`/integrations_api/telemetry/getNetworkErrorLogs`, body, config, token);

type ApiRequestLogParams = {
  apiRequestIds: string[];
  teamId: number;
  dataType: string;
};
// dataType narrows the IS lookup to the entity_mapping partitions that carry it (INTG-14243)
const withDataTypeParam = <C extends { params?: object }>(config: C, dataType?: string) =>
  dataType ? { ...config, params: { ...config.params, dataType } } : config;

export const getSingleEntity = (
  params: { entityId: number; dataType?: string },
  config = {},
  token?: string,
) =>
  getApiGalaxyServer(
    `/integrations_api/telemetry/findEntity/${params.entityId}`,
    withDataTypeParam(config, params.dataType),
    token,
  );

export const getApiRequestLog = (params: ApiRequestLogParams, config = {}, token?: string) => {
  return getApiGalaxyServer(
    `/rails_api/integrations/api_request_logs`,
    {
      ...config,
      params: {
        team_id: params.teamId,
        data_type: params.dataType,
        api_request_ids: params.apiRequestIds,
      },
    },
    token,
  );
};

export const getActiveIntegrations = (config = {}, token?: string) =>
  getApiGalaxyServer(`/integrations_api/telemetry/integrationCounts`, config, token);

export const getIntegrations = ({
  params,
  token,
  config,
}: BaseApiParams<FetchIntegrationsParams>) =>
  getApiGalaxyServer(
    `/integrations_api/configs/integrations`,
    {
      ...config,
      params: params as any,
    },
    token,
  );

/* --------------------------- Integration Health --------------------------- */
export const getIntegrationHealth = (
  params: Omit<fetchIntegrationHealthParams, 'isoStateId'>,
  config = {},
  token?: string,
) =>
  postApiGalaxyServer(
    `/integrations_api/forward/${params.targetService}/integrationHealth/getHealth`,
    omit(params, 'targetService'),
    config,
    token,
  );

/* --------------------------- Csv Uploads --------------------------- */
export const getCsvImportStatus = (
  params: FetchCsvImportStatusParams,
  body: FetchCsvImportStatusPayload,
  config = {},
  token?: string,
) =>
  postApiGalaxyServer(
    `/integrations_api/csvImport/getCsvImportStatus/${params.targetServiceId}`,
    body,
    config,
    token,
  );

/* --------------------------- Update Integration Notes --------------------------- */
export const updateIntegrationNotes = (
  params: UpdateIntegrationNotesParams,
  config = {},
  token?: string,
) => {
  const body = {
    notes: params.notes,
  };

  return postApiGalaxyServer(
    `/integrations_api/telemetry/setIntegrationNotes/${params.targetServiceId}`,
    body,
    config,
    token,
  );
};

/* --------------------------- Entity Error --------------------------- */
export const getEntityErrors = (
  { entityId, entityUUID, limit, targetServiceId }: FetchEntityErrorsParams,
  config = {},
  token?: string,
) => {
  const configToUse = {
    ...config,
    params: { entityId, entityUUID, limit },
  };

  return getApiGalaxyServer(
    `/integrations_api/entity-error/${targetServiceId}`,
    configToUse as any,
    token,
  );
};

export const checkPendingEntityStatus = (
  { targetServiceId, entityId, dataType }: FetchPendingEntityStatusPayload,
  config = {},
  token?: string,
) => {
  return getApiGalaxyServer(
    `/integrations_api/mappings/check/${targetServiceId}/${entityId}`,
    withDataTypeParam(config, dataType),
    token,
  );
};

/* -------------------------------------------------------------------------- */
export const getDataCountsByEntity = (
  params: FetchDataCountsByDataTypeParams,
  body: Omit<FetchDataCountsByDataTypeParams, 'onSuccess' | 'targetServiceId'>,
  config = {},
  token?: string,
) => {
  return postApiGalaxyServer(
    `/integrations_api/telemetry/dataCount/${params.targetServiceId}`,
    body,
    config,
    token,
  );
};

/* -------------------------------------------------------------------------- */
export const unarchiveIntegration = (
  payload: UnarchiveIntegrationPayload,
  config = {},
  token?: string,
) => {
  return postApiGalaxyServer(
    `/integrations_api/provision/recovery/${payload.teamId}/${payload.targetServiceId}`,
    {},
    config,
    token,
  );
};

export const fetchEntityMappings = (
  params: Omit<FetchEntityMappingsParams, 'onSuccess'>,
  config = {},
  token?: string,
) => {
  return postApiGalaxyServer(
    `/integrations_api/mappings/find/${params.teamId}/${params.targetServiceId}/${params.dataType}`,
    params.body,
    config,
    token,
  );
};

export const fetchIntegrationAgentDomains = (config = {}, token?: string) =>
  getApiGalaxyServer(`/integrations_api/configs/agent-domains`, config, token);

export const fetchIntegrationSettingSchema = (
  targetServiceId: number,
  config: Record<string, any> = {},
  token = '',
) =>
  getApiGalaxyServer(
    `/integrations_api/provision/integrationsSettingSchema/${targetServiceId}`,
    config,
    token,
  );

export const fetchIntegrationStoredProcedures = (
  params: FetchIntegrationStoredProceduresPayload,
  config: Record<string, any> = {},
  token = '',
) => {
  return getApiGalaxyServer(
    `/integrations_api/forward/${params.targetService}/${params.integrationName}/storedProcedures/${params.targetServiceId}`,
    config,
    token,
  );
};

export const updatePhaseOrder = (
  { targetServiceId }: { targetServiceId: number },
  config: Record<string, any> = {},
  token = '',
) =>
  postApiGalaxyServer(
    `/integrations_api/mappings/utils/update-phase-order/${targetServiceId}`,
    {},
    config,
    token,
  );
